import { useEffect, useState } from "react";
import { useStoreActions, useStoreState } from "../../store";
import { LabelText, StyledReportIssuePage } from "./ReportIssuePage.styles";
import { HeaderMode } from "../../types/HeaderMode";
import {
  MenuComponent,
  MenuEventArgs,
  MenuItemModel,
} from "@syncfusion/ej2-react-navigations";
import { useAuth } from "../../utility/useAuth";
import { useNavigate } from "react-router";
import { Col, Container, Row } from "react-grid-system";
import { IssueDetails } from "../../types/IssueDetails";
import { projectName, projectVersion } from "../../config";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import { reportIssue } from "../../services/ApiService";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import { ListBoxComponent } from "@syncfusion/ej2-react-dropdowns/src/list-box";
import { CaptureImageModal } from "../../components/Modals/CaptureImageModal/CaptureImageModal";
import { Attachment } from "../../types/Attachment";
import { fileToBase64, getFileExtension } from "../../utility/pathHelper";
import { useFilePicker } from "use-file-picker";
import JSZip from "jszip";

export const ReportIssuePage: React.FC = () => {
  let menuObj: MenuComponent | null;
  const auth = useAuth();
  const navigate = useNavigate();
  const user = useStoreState((state) => state.user);
  const { setHeaderMode } = useStoreActions((actions) => actions.appSettings);

  const { openFilePicker, filesContent, loading } = useFilePicker({
    readAs: "ArrayBuffer",
    accept: "*",
    multiple: false,
    readFilesContent: true,
    onFilesSuccessfullySelected: ({ plainFiles, filesContent }) => {
      // this callback is called when there were no validation errors
      console.log("onFilesSuccessfullySelected", plainFiles, filesContent);
      const attachment: Attachment = {
        fileName: filesContent[0].name,
        content: new Blob([filesContent[0].content], {
          type: filesContent[0].type,
        }),
      };
      let atts = [...attachments];
      atts.push(attachment);
      setAttachments(atts);
    },
  });

  let textareaObj: any;

  function onCreateMultiText() {
    textareaObj.addAttributes({ rows: 1 });
    textareaObj.respectiveElement.style.height = "auto";
    textareaObj.respectiveElement.style.height = "300px";
  }

  const initIssue: IssueDetails = {
    user: user.info.name,
    application: `${projectName} ${projectVersion}`,
    issueDescription: "",
    reproSteps: "",
    isException: false,
  };

  const [issue, setIssue] = useState<IssueDetails>(initIssue);

  const issueDesctrionMandatory = "The Issue Description field is required";
  const [error, setError] = useState(issueDesctrionMandatory);
  const [showCaptureImageModal, setShowCaptureImageModal] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  useEffect(() => {
    setHeaderMode(HeaderMode.ReportIssue);
  }, []);

  const menuItems: MenuItemModel[] = [
    { id: "back", text: "Back", iconCss: "e-icons e-back" },
    { id: "addFile", text: "Add", iconCss: "e-icons e-folder-open" },
    { id: "captureImage", text: "Capture", iconCss: "e-icons e-video" },
    { id: "sendReport", text: "Send", iconCss: "e-icons e-send" },
  ];

  const toolTips = new Map<string, string>([
    ["sendReport", "Report an Issue"],
    ["addFile", "Add File"],
    ["captureImage", "Capture Image"],
  ]);

  function beforeItemRender(args: MenuEventArgs): void {
    args.element.setAttribute("title", toolTips.get(args.item.id ?? "") ?? "");
  }

  function created(): void {
    // Disable items
    if (menuObj) {
      menuObj.enableItems(["Send"], false, false);
    }
  }

  async function selectMenuItem(args: MenuEventArgs): Promise<void> {
    const id = args.item.id;

    if (id === "sendReport") {
      let issueToSent = issue; 
      if (attachments.length > 0) { 
        const zip = new JSZip();
        attachments.forEach((file, index) => {
          zip.file(`${file.fileName}`, file.content);
        });

        const zipContent = await zip.generateAsync({ type: "blob" });
        issueToSent.diagnosticContent = await fileToBase64(zipContent);
      }

      reportIssue(auth, issueToSent)
        .then(() => {
          ToastUtility.show(
            `The issue report has been sent successfully.`,
            "Info",
            2000,
          );
          navigate(-1);
        })
        .catch((error) => {
          const message = `Failed to sent the issue report. Error: ${error}`;
          console.error(message);
          ToastUtility.show(message, "Error", 2000);
        });
    } else if (id === "captureImage") {
      setShowCaptureImageModal(true);
    } else if (id === "addFile") {
      openFilePicker();
    } else if (id === "back") {
      navigate(-1);
    }
  }

  function onAddImage(folder: string, imageName: string, imageContent: Blob) {
    setShowCaptureImageModal(false);
    if (imageName?.length > 0 && imageContent) {
      const attachment: Attachment = {
        fileName: imageName,
        content: imageContent,
      };
      let atts = [...attachments];
      atts.push(attachment);
      setAttachments(atts);
    }
  }

  const getUniqueFileName = (): string => {
    let imageName: string = "Image";
    let i = 0;
    while (attachments.some((a) => a.fileName === `${imageName}.png`)) {
      ++i;
      imageName = `${"Image"} (${i})`;
    }
    return imageName;
  };

  function Template(data: any): JSX.Element {
    const [img, setImg] = useState<string>();

    const fetchImage = async () => {
      if (getFileExtension(data.fileName) === "png") {
        const imageObjectURL = URL.createObjectURL(data.content);
        setImg(imageObjectURL);
      }
    };

    useEffect(() => {
      fetchImage();
    }, []);

    return (
      //<div className="list-wrapper" style={{ width: 400 }}>
      <Container fluid style={{ margin: 10 }}>
        <Row align="stretch">
          <span>{`${data.fileName}`}</span>
        </Row>
        {img && (
          <Row align="stretch">
            <img height={120} width={120} src={img} />
          </Row>
        )}
      </Container>
      //</div>
    );
  }

  return (
    <StyledReportIssuePage style={{ height: 800 }}>
      <Container fluid>
        <Row>
          <Col>
            <Row align="stretch">
              <MenuComponent
                ref={(scope: MenuComponent) => (menuObj = scope)}
                items={menuItems}
                select={selectMenuItem}
                beforeItemRender={beforeItemRender}
                created={created}
              />
            </Row>
            <Row align="stretch">
              <Col sm={2}>
                <LabelText>Application:</LabelText>
              </Col>
              <Col>
                <TextBoxComponent value={issue?.application} readOnly />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={2}>
                <LabelText>User:</LabelText>
              </Col>
              <Col>
                <TextBoxComponent value={issue?.user} readOnly />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={2}>
                <LabelText>Issue Description:</LabelText>
              </Col>
              <Col>
                <TextBoxComponent
                  value={issue?.issueDescription}
                  input={(args: any) => {
                    let newIssue = { ...issue };
                    newIssue.issueDescription = args?.value;
                    setIssue(newIssue);
                    if (menuObj) {
                      menuObj.enableItems(
                        ["Send"],
                        newIssue.issueDescription?.length > 0,
                        false,
                      );
                    }
                    if (newIssue.issueDescription?.length > 0) {
                      setError("");
                    } else {
                      setError(issueDesctrionMandatory);
                    }
                  }}
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={2}>
                <LabelText>Steps to replicate:</LabelText>
              </Col>
              <Col>
                <TextBoxComponent
                  ref={(scope: TextBoxComponent) => {
                    textareaObj = scope;
                  }}
                  multiline={true}
                  placeholder="Enter steps to replicate the issue"
                  value={issue?.reproSteps}
                  created={onCreateMultiText}
                  input={(args: any) => {
                    let newIssue = { ...issue };
                    newIssue.reproSteps = args?.value;
                    setIssue(newIssue);
                  }}
                />
              </Col>
            </Row>
            <Row align="stretch">
              <h5 style={{ color: "red" }}>{error}</h5>
            </Row>
          </Col>
          <Col sm={5}>
            <Row align="stretch">
              <LabelText>Support files:</LabelText>
              <ListBoxComponent
                id="listbox"
                //ref={(scope: ListBoxComponent) => (listboxObj = scope)}
                height={650}
                dataSource={attachments}
                selectionSettings={{ mode: "Single" }}
                itemTemplate={Template}
                //change={onImageSelected}
                fields={{ text: "fileName" }}
              />
            </Row>
          </Col>
        </Row>
      </Container>
      {showCaptureImageModal && (
        <CaptureImageModal
          open={showCaptureImageModal}
          folder={""}
          initImageName={getUniqueFileName()}
          onClose={() => {
            setShowCaptureImageModal(false);
          }}
          onAddImage={onAddImage}
        />
      )}
    </StyledReportIssuePage>
  );
};
